/**
 * @file CompanyRowActions.tsx
 * @brief Per-row dropdown menu for the Companies table.
 *
 * @details
 * Available actions adapt to the company's current `status`:
 * - active     → Edit / Suspend / Delete
 * - suspended  → Edit / Reactivate / Delete
 * - deleted    → Edit only
 *
 * Suspend and Delete prompt via `<AlertDialog>` because both cascade
 * to every employee of the company.
 *
 * @author DES
 * @date 26-05-2026
 */

import { Ellipsis, Pencil, Shield, ShieldOff, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { deferAfterDropdownMenu } from "@/shared/utils/restoreBodyInteraction";
import {
  useDeleteCompany,
  useReactivateCompany,
  useSuspendCompany,
} from "../api";
import type { AdminCompany } from "../schemas";

interface CompanyRowActionsProps {
  company: AdminCompany;
  onEdit: (company: AdminCompany) => void;
}

type ConfirmKind = "suspend" | "delete" | null;

export function CompanyRowActions({ company, onEdit }: CompanyRowActionsProps) {
  const [confirm, setConfirm] = useState<ConfirmKind>(null);

  const suspend = useSuspendCompany();
  const reactivate = useReactivateCompany();
  const remove = useDeleteCompany();

  const runMutation = async (
    action: () => Promise<unknown>,
    successMessage: string,
  ) => {
    try {
      await action();
      toast({ variant: "success", title: successMessage });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Action failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  const handleSuspend = () =>
    runMutation(
      () => suspend.mutateAsync(company.id),
      `${company.name} suspended`,
    );

  const handleReactivate = () =>
    runMutation(
      () => reactivate.mutateAsync(company.id),
      `${company.name} reactivated`,
    );

  const handleDelete = () =>
    runMutation(() => remove.mutateAsync(company.id), `${company.name} deleted`);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={`Actions for ${company.name}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Ellipsis className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem
            onSelect={() => deferAfterDropdownMenu(() => onEdit(company))}
          >
            <Pencil className="h-4 w-4" />
            Edit
          </DropdownMenuItem>

          {company.status === "active" ? (
            <DropdownMenuItem
              onSelect={() =>
                deferAfterDropdownMenu(() => setConfirm("suspend"))
              }
              destructive
            >
              <ShieldOff className="h-4 w-4" />
              Suspend
            </DropdownMenuItem>
          ) : company.status === "suspended" ? (
            <DropdownMenuItem onSelect={handleReactivate}>
              <Shield className="h-4 w-4" />
              Reactivate
            </DropdownMenuItem>
          ) : null}

          {company.status !== "deleted" ? (
            <DropdownMenuItem
              onSelect={() =>
                deferAfterDropdownMenu(() => setConfirm("delete"))
              }
              destructive
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* ----------------------------------------------------------------------
          Suspend confirmation — note the cascade warning.
          ---------------------------------------------------------------------- */}
      <AlertDialog
        open={confirm === "suspend"}
        onOpenChange={(open) => !open && setConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend this company?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">
                {company.name}
              </span>{" "}
              and{" "}
              <span className="font-medium text-foreground">
                {Number(company.user_count ?? 0).toLocaleString()} employee
                {Number(company.user_count ?? 0) === 1 ? "" : "s"}
              </span>{" "}
              will be suspended. None of their accounts will be able to sign
              in until reactivated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-warning text-warning-foreground hover:bg-warning/90"
              onClick={async () => {
                setConfirm(null);
                await handleSuspend();
              }}
            >
              Suspend
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ----------------------------------------------------------------------
          Delete confirmation — soft delete + cascade warning.
          ---------------------------------------------------------------------- */}
      <AlertDialog
        open={confirm === "delete"}
        onOpenChange={(open) => !open && setConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this company?</AlertDialogTitle>
            <AlertDialogDescription>
              This is a soft delete. The company is marked deleted and so are
              all{" "}
              <span className="font-medium text-foreground">
                {Number(company.user_count ?? 0).toLocaleString()} employee
                {Number(company.user_count ?? 0) === 1 ? "" : "s"}
              </span>
              . Historical sessions and invoices are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                setConfirm(null);
                await handleDelete();
              }}
            >
              Delete company
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
